import {
  Button,
  DescriptionDetails,
  DescriptionList,
  DescriptionListItem,
  DescriptionTerm,
  Drawer,
  Icon,
  Loader,
  Panel,
  SectionHeader,
  Tag,
  TagLabel,
  Toast
} from '@deque/cauldron-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Separator from '~/common/components/Separator';
import { useAuthContext } from '~/common/contexts/auth';
import { useFeatureFlagState } from '~/common/contexts/featureFlags';
import { formatSupportInfoText } from './formatSupportInfoText';
import styles from './SupportInfoDrawer.css';
import {
  purchaseStateLabel,
  useSupportInfo,
  type ProductAccessRow
} from './useSupportInfo';
import useToggleShortcut from './useToggleShortcut';

/**
 * A read-only diagnostics drawer (opened with Ctrl/Cmd + Shift + .) that
 * surfaces the signed-in user's Keycloak id, active enterprise, product access
 * levels, admin status, and enterprise memberships, with a "Copy all" action.
 * Mounted once at the app root and gated behind `support_info_drawer_v1`.
 */
const SupportInfoDrawer = (): React.ReactElement | null => {
  const { t } = useTranslation();
  const flagEnabled = useFeatureFlagState('support_info_drawer_v1');
  const { isAuthenticated, billingUser } = useAuthContext();
  const info = useSupportInfo();
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'confirmation' | 'error';
  } | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Clear the copy toast when the drawer closes so it doesn't reappear on reopen.
  useEffect(() => {
    if (!open) {
      setToast(null);
    }
  }, [open]);

  const enabled = flagEnabled && isAuthenticated;

  const toggle = useCallback(() => setOpen(previous => !previous), []);
  const close = useCallback(() => setOpen(false), []);

  useToggleShortcut({ enabled, onToggle: toggle });

  const copy = useCallback(async () => {
    try {
      const text = formatSupportInfoText(
        info,
        {
          // Strip query string and hash — portal URLs can carry SSO codes /
          // invitation tokens that must never be copied.
          url: `${window.location.origin}${window.location.pathname}`,
          generatedAt: new Date().toISOString(),
          billingUser
        },
        t
      );
      await navigator.clipboard.writeText(text);
      setToast({ message: t('Copied to clipboard.'), type: 'confirmation' });
    } catch {
      setToast({ message: t('Failed to copy.'), type: 'error' });
    }
  }, [info, billingUser, t]);

  if (!enabled) {
    return null;
  }

  const recognizedProducts = info.products.filter(row => row.recognized);
  const otherEntitlements = info.products.filter(row => !row.recognized);

  const renderProducts = (rows: ProductAccessRow[]) => (
    <div className={styles.productList}>
      {rows.map(row => (
        <Panel key={row.id}>
          <div className={styles.productRow}>
            <h4 className={styles.productName}>{row.name}</h4>
            <Tag className={styles.tag}>
              <TagLabel>{purchaseStateLabel(row.state, t)}</TagLabel>
            </Tag>
          </div>
        </Panel>
      ))}
    </div>
  );

  // Cauldron's Drawer (6.24.0) renders a generic <div>, so screen readers
  // ignore `aria-label` without an explicit dialog role. Set role + aria-modal
  // here (the drawer is modal by default) until dequelabs/cauldron#2428 ships
  // these semantics natively.
  return (
    <Drawer
      open={open}
      onClose={close}
      position="right"
      role="dialog"
      aria-modal="true"
      aria-label={t('Debug panel')}
      focusOptions={{ initialFocus: headingRef }}
      className={styles.drawer}
    >
      <Panel className={styles.panel}>
        {/*
          Only mount the Toast while there is a message to show. Cauldron's
          Toast always renders a focusable dismiss <button> regardless of its
          `show` prop, and as the Panel's first child it would otherwise be the
          first focusable element the drawer's focus trap wraps to — stealing
          the wrap target from "Copy all" so focus never returns to it on
          repeated tab passes (#14936).
        */}
        {toast && (
          <Toast show type={toast.type} onDismiss={() => setToast(null)}>
            {toast.message}
          </Toast>
        )}
        <div className={styles.header}>
          <h2 ref={headingRef} tabIndex={-1} className={styles.title}>
            {t('Debug panel')}
          </h2>
          <div className={styles.headerActions}>
            <Button variant="primary" onClick={copy}>
              {t('Copy all')}
            </Button>
            <Button variant="secondary" onClick={close}>
              <Icon type="close" />
              {t('Close')}
            </Button>
          </div>
        </div>
        <Separator />
        {info.loading ? (
          <Loader label={t('Loading')} />
        ) : (
          <>
            <SectionHeader heading={<h3>{t('Identity')}</h3>} />
            <DescriptionList>
              <DescriptionListItem>
                <DescriptionTerm>{t('Keycloak ID')}</DescriptionTerm>
                <DescriptionDetails>
                  <code className={styles.code}>
                    {info.keycloakId ?? t('N/A')}
                  </code>
                </DescriptionDetails>
              </DescriptionListItem>
              <DescriptionListItem>
                <DescriptionTerm>{t('Active enterprise')}</DescriptionTerm>
                <DescriptionDetails>
                  {info.activeEnterprise ? (
                    <span className={styles.stacked}>
                      <span>{info.activeEnterprise.name}</span>
                      <code className={styles.code}>
                        {info.activeEnterprise.id}
                      </code>
                    </span>
                  ) : (
                    t('N/A')
                  )}
                </DescriptionDetails>
              </DescriptionListItem>
              <DescriptionListItem>
                <DescriptionTerm>{t('Enterprise admin')}</DescriptionTerm>
                <DescriptionDetails>
                  <Tag className={styles.tag}>
                    <TagLabel>
                      {info.isEnterpriseAdmin ? t('Yes') : t('No')}
                    </TagLabel>
                  </Tag>
                </DescriptionDetails>
              </DescriptionListItem>
            </DescriptionList>

            <Separator />
            <SectionHeader heading={<h3>{t('Product access')}</h3>} />
            {info.enterprises.length > 0 && (
              <p className={styles.note}>
                {t('Product access is shown for your active enterprise only.')}
              </p>
            )}
            {recognizedProducts.length === 0 ? (
              <p className={styles.empty}>{t('No product subscriptions')}</p>
            ) : (
              renderProducts(recognizedProducts)
            )}

            {otherEntitlements.length > 0 && (
              <>
                <Separator />
                <SectionHeader heading={<h3>{t('Other entitlements')}</h3>} />
                {renderProducts(otherEntitlements)}
              </>
            )}

            <Separator />
            <SectionHeader
              heading={<h3>{t('Enterprises you belong to')}</h3>}
            />
            {info.enterprises.length === 0 ? (
              <p className={styles.empty}>{t('N/A')}</p>
            ) : (
              <ul className={styles.list}>
                {info.enterprises.map(enterprise => (
                  <li key={enterprise.id} className={styles.row}>
                    <span className={styles.rowName}>{enterprise.name}</span>
                    <code className={styles.code}>{enterprise.id}</code>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </Panel>
    </Drawer>
  );
};

export default SupportInfoDrawer;
